import { useMemo } from 'react';
import { WorkOrderItemData } from '../index';
import { useWorkorderDetailContext } from '../../../WorkorderDetailProvider';
import { message, notification } from 'antd';
import { useDisclosure } from '@/hooks';
import { useWorkorderItemPermissions } from '../../WorkOrderItemPartForm/workorderItemPermissions';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { WorkOrderComplementStatus } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { usePartStockPrices } from '@/core/domain/stock/StockItems/StockItems.hooks';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import formatterPrice from '@/utils/number/formatterPrice';
import WorkOrderItemSpan from '../WorkOrderItemSpan';
import Authorities from '@/types/Authority';
import InputDecimalCell from '@/components/_common/InputDecimalCell';
import useWorkOrderItemUpdate from '../WorkOrderItemUtils/useWorkOrderItemUpdate';
import { isAppPortocar } from '@/index';
import { PortoApoliceType } from '@/core/domain/porto-seguro/apolices/apolices.types';

interface Props {
  item: WorkOrderItemData;
}

export default function WorkOrderItemDiscountValue(props: Props) {
  const [isInputEnabled, inputAction] = useDisclosure(false);
  const itemPermissions = useWorkorderItemPermissions();

  const {
    workorder: { data: workorder },
    complement: { data: complement },
    company: { companyConfig },
  } = useWorkorderDetailContext();

  const isPart = props.item.type === WorkOrderItemType.Part;
  const isService = props.item.type === WorkOrderItemType.Service;
  const subTotal = (props.item.quantity || 0) * (props.item.unitPrice || 0);
  const hasPolicy = !!complement?.policyNumber;

  const update = useWorkOrderItemUpdate(props.item);

  const partStockPrices = usePartStockPrices(
    props.item.id,
    isPart && isInputEnabled,
  );

  const stockPrices = useMemo(
    () =>
      partStockPrices.data?.result.find(
        (part) => part.company.id === workorder?.company.id,
      ),
    [workorder?.company.id, partStockPrices, partStockPrices.dataUpdatedAt],
  );

  const hasLowValuePermission = hasPermissions([
    Authorities.MOVIMENTATION_WORK_ORDER_ALLOW_TO_ENTER_ITEM_SALES_VALUE_BELOW_PROFIT_MARGIN,
  ]);

  const getMaxDiscountPart = () => {
    let max = undefined;
    const companyMaxDiscount = companyConfig?.maxPartDiscount ?? 0;

    if (!itemPermissions.giveDiscountOverLimit && stockPrices?.maxDiscount) {
      max = subTotal * stockPrices.maxDiscount;
    } else if (
      !itemPermissions.addDiscountGreaterDefinedByCompany &&
      typeof companyMaxDiscount === 'number'
    ) {
      max = subTotal * companyMaxDiscount;
    }

    return max;
  };

  const getMaxServiceDiscount = () => {
    const permission =
      !itemPermissions.addDiscountGreaterDefinedByCompany &&
      !hasPermissions([
        Authorities.MOVIMENTATION_WORKORDER_GIVE_DISCOUNT_OVER_LIMIT,
      ]);
    const maxDiscount = subTotal * Number(companyConfig?.maxServiceDiscount);

    return permission && maxDiscount ? maxDiscount : undefined;
  };

  const getMinDiscount = () => {
    if (
      isAppPortocar &&
      isService &&
      !workorder?.partnerDiscount &&
      workorder?.employeeDiscount
    )
      return subTotal * 0.1;

    if (
      !isAppPortocar ||
      !isService ||
      !complement?.policyNumber ||
      !workorder?.partnerDiscount
    )
      return undefined;

    const isPortoPolicy =
      hasPolicy &&
      complement?.companyCode?.toUpperCase() === PortoApoliceType.PORTO;

    return subTotal * (isPortoPolicy ? 0.2 : 0.15);
  };

  const handleUpdateDiscountValue = async (discountValue: number) => {
    const discount = (discountValue || 0) / (subTotal || 0);
    const maxDiscount = isPart ? getMaxDiscountPart() : getMaxServiceDiscount();
    const minDiscount = getMinDiscount();

    const sellPrice = stockPrices?.sellPrice || 0;
    const totalItem = props.item.unitPrice - discountValue;

    if (!hasLowValuePermission && totalItem < sellPrice) {
      return notification.info({
        message: `Você não tem permissão para lançar valor total de venda do item abaixo da margem de lucro ${formatterPrice(
          sellPrice || 0,
        )}`,
      });
    }

    if (!!maxDiscount && discountValue > maxDiscount) {
      message.destroy();
      return message.warn(
        `Desconto não pode ser maior que ${formatterPrice(maxDiscount)}`,
      );
    }

    if (!!minDiscount && discountValue < minDiscount) {
      message.destroy();
      return message.warn(
        `Desconto não pode ser menor que ${formatterPrice(minDiscount)}`,
      );
    }

    if (!itemPermissions.allowSellingBellowCostValue) {
      const subTotal = (props.item.quantity || 0) * (props.item.unitPrice || 0);

      const amount = subTotal - discount * 100;

      if (amount < (props.item.unitCost || 0) * (props.item.quantity || 0)) {
        message.destroy();
        return message.warn(
          'O preço de venda deve ser maior que o custo unitário',
        );
      }
    }

    try {
      await update.mutateAsync({ key: 'discount', value: discount });
    } finally {
      inputAction.close();
    }
  };

  const permissionPart =
    !props.item.unitPrice ||
    (complement?.status !== WorkOrderComplementStatus.Open &&
      !itemPermissions.openedItemChangeDiscount) ||
    !itemPermissions.editDiscountValue;

  const permissionService =
    !props.item.unitPrice ||
    (!itemPermissions.addDiscountGreaterDefinedByCompany &&
      !companyConfig?.maxServiceDiscount) ||
    (!props.item.isInternal && !itemPermissions.giveDiscountOutsourcedServices);

  if (isInputEnabled) {
    return (
      <InputDecimalCell
        defaultValue={
          props.item.type === WorkOrderItemType.Kit
            ? props.item.groupDiscountValue || 0
            : props.item.quantity * props.item.unitPrice * props.item.discount
        }
        min={0}
        max={subTotal}
        loading={update.isLoading}
        onConfirmValue={(discountValue) =>
          handleUpdateDiscountValue(discountValue)
        }
        onClose={inputAction.close}
      />
    );
  }

  return (
    <WorkOrderItemSpan
      item={props.item}
      onClick={inputAction.open}
      permission={isPart ? permissionPart : permissionService}
    >
      {formatterPrice(
        props.item.type === WorkOrderItemType.Kit
          ? props.item.groupDiscountValue || 0
          : props.item.quantity * props.item.unitPrice * props.item.discount,
      )}
    </WorkOrderItemSpan>
  );
}
